import React from "react";
import IconByName from "../IconByName";
import "./styles/atoms.css";
import "./styles/Cards.css";

/**
 * Grid wrapper. `cols` is the desktop column count; it collapses to 2
 * at 980px and 1 at 860px automatically.
 *
 * <CardGrid cols={3}> … </CardGrid>
 */
export function CardGrid({ children, cols = 3, className = "" }) {
  return (
    <div className={`rs-grid ${className}`} data-cols={cols}>
      {children}
    </div>
  );
}

/**
 * Icon + title + text. The icon is optional, which covers Beratung's
 * text-only variant without a separate component.
 *
 * `layout="row"` lays the icon beside the text instead of above it —
 * this covers Lernen & Verstehen's mini-cards and week-grid cells,
 * which are the same shape at two sizes rather than a new component.
 *
 * `headingLevel` fixes the h2/h3 drift between pages — default h3.
 */
export function IconCard({ iconName, icon, title, text, headingLevel = 3, layout = "column", size, email, website, children }) {
  const Heading = `h${headingLevel}`;
  const iconNode = icon || (iconName ? <IconByName name={iconName} /> : null);
  const style = size ? { "--rs-icon-size": size } : undefined;
  const hasActions = Boolean(email || website);

  return (
    <article className={`rs-card rs-card--icon rs-card--icon-${layout}`}>
      {hasActions && (
        <div className="rs-card__corners">
          {email && (
            <a className="rs-card__corner" href={`mailto:${email}`} aria-label={`E-Mail${title ? ` an ${title}` : ""}`}>
              <IconByName name="mail" size={18} />
            </a>
          )}
          {website && (
            <a className="rs-card__corner" href={website} target="_blank" rel="noopener noreferrer" aria-label={`Website${title ? ` von ${title}` : ""} öffnen`}>
              <IconByName name="globe" size={18} />
            </a>
          )}
        </div>
      )}
      {iconNode && (
        <div className="rs-card__icon" style={style} aria-hidden="true">
          {iconNode}
        </div>
      )}
      <div className="rs-card__icon-text">
        <Heading>{title}</Heading>
        {text && <p>{text}</p>}
        {children}
      </div>
    </article>
  );
}

/**
 * Image + optional icon + title + text + tags + corner link.
 * Covers Räume rooms, Nachmittagsbetreuung Kurse, Begabung Angebote
 * Kurse and Partner cards — which were four separate components.
 *
 * `mediaHeight` lets Partner logos sit shorter than room photos
 * without a new class.
 *
 * Without an image the card gets `rs-card--no-media`, which gives
 * the title more presence (see Cards.css) — otherwise a text-only
 * card has nothing visually anchoring it.
 */
export function MediaCard({
  image,
  imageAlt,
  iconName,
  icon,
  title,
  text,
  tags,
  href,
  hrefLabel,
  hrefIcon = "globe",
  mediaHeight,
  headingLevel = 3,
  children,
}) {
  const Heading = `h${headingLevel}`;
  const iconNode = icon || (iconName ? <IconByName name={iconName} /> : null);
  const style = mediaHeight ? { "--rs-media-h": mediaHeight } : undefined;
  const hasMedia = Boolean(image?.url);

  const classes = [
    "rs-card",
    "rs-card--media",
    hasMedia ? "" : "rs-card--no-media",
    href ? "rs-card--has-corner" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={classes} style={style}>
      {href && (
        <a
          className="rs-card__corner"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={hrefLabel || `Website von ${title} öffnen`}
        >
          <IconByName name={hrefIcon} size={18} />
        </a>
      )}

      {hasMedia && (
        <figure className="rs-card__media">
          <img src={image.url} alt={image.alt || imageAlt || title || ""} loading="lazy" />
        </figure>
      )}

      <div className="rs-card__body">
        {iconNode && (
          <div className="rs-card__icon" aria-hidden="true">
            {iconNode}
          </div>
        )}
        <Heading>{title}</Heading>
        {text && <p>{text}</p>}

        {tags?.length ? (
          <div className="rs-tags" aria-label="Eigenschaften">
            {tags.map((tag) => (
              <span className="rs-tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {children}
      </div>
    </article>
  );
}

export default CardGrid;